import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { useTheme } from '@mui/material/styles';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { useContext, useEffect, useRef, useState } from "react";
import LicenceCheck from "../../Authorisation/LicenceCheck";
import RankOrderScatterPlot from "../../Charts/RankOrder/RankOrderScatterplot";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";
import PDFLink from "../../Popups/PDFLink";
import WarningPopup from "../../Popups/WarningPopup";
import surveyDayTimeDropdown from "../../Utilities/Constants/SurveyDayTimeDropdown";
import DataContainer from "../../Utilities/DataContainer";
import Line from "../../Utilities/Line";
import { TimePair } from "../../Utilities/ViewModels/TimePair";
import TripRateSitesGraphDialog from "../../Charts/TripRate/TripRateSitesGraphDialog";
import { TripRateSitesGraphDirection, TripRateSitesGraphView } from "../../Charts/TripRate/TripRateSitesGraphTypes";
import ModeSelectionViewModel from "../ViewModels/ModeSelectionViewModel";
import RankOrderPDFViewModel from "../ViewModels/RankOrderPDFViewModel";
import RankOrderViewModel from "../ViewModels/RankOrderViewModel";
import TripRateCalculationFiltersViewModel from "../ViewModels/TripRateCalculationFiltersViewModel";
import RankOrderExportDialog from "./RankOrderExportDialog";
import RankOrderPeriodDialog from "./RankOrderPeriodDialog";
import RankOrderTable from "./RankOrderTable";
import RankOrderExportViewModel from "../ViewModels/RankOrderExportViewModel";
import { SurveyViewModel } from "../../Contexts/SessionState";
import DeselectedSurveysViewModel from "../Calculation/DeselectedSurveysViewModel";
import GetRankOrderTableViewModel from "../../Charts/RankOrder/ViewModels/GetRankOrderTableViewModel";
import { useLocation, useNavigate } from "react-router-dom";
import { useTripRateContext } from "../TripRateContext";
import { getFilteredSurveyRefs } from "../../TripRateNew/TriprateFilters";
import { getValidActiveSurveyRefs } from "../TripRateSurveyRefs";

export default function RankOrderPage() {
    const theme = useTheme();
    const { sessionState } = useContext(SessionContext);
    const { surveys, loading: surveysLoading } = useTripRateContext();
    const selectedSurveyRefs = getValidActiveSurveyRefs(
        sessionState.selectedSitesAndSurveys,
        getFilteredSurveyRefs(surveys),
    );
    const selectedSurveyRefsKey = JSON.stringify(selectedSurveyRefs);
    const { loading, post, postPDF, postCSV } = useContext(APIContext);
    const [showGraph, setShowGraph] = useState(false);
    const [modes, setModes] = useState<ModeSelectionViewModel[]>([]);
    const [exportDialog, setExportDialog] = useState(false);
    const [vehicleType, setVehicleType] = useState(0);
    const [vehicleLabel, setVehicleLabel] = useState("Total Vehicles");
    const [exportData, setExportData] = useState<RankOrderViewModel>(new RankOrderViewModel());
    const [clickToggleValue, setClickToggleValue] = useState<string>('site');
    const [searchValue, setSearchValue] = useState('');
    const [siteGraphOpen, setSiteGraphOpen] = useState(false);
    const [siteGraphView, setSiteGraphView] = useState<TripRateSitesGraphView>("selection");
    const [siteGraphMode, setSiteGraphMode] = useState<number | null>(null);
    const [siteGraphDirection, setSiteGraphDirection] = useState<TripRateSitesGraphDirection>("Total");

    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('toTab');
    const selectionParam = params.get('selectionType');

    const formatPair = ({ start, end }: TimePair) => `${getLabel(start)}-${getLabel(end)}`;
    const getLabel = (id: number) => surveyDayTimeDropdown.find(x => x.id === id)?.label ?? "";
    const toDateOnly = (value?: string | null) => {
        if (!value) return null;

        const parts = value.split("-");
        if (parts.length === 3 && parts[2].length === 4) {
            const [day, month, year] = parts;
            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
    };

    const getModesStartDate = () => {
        return selectedSurveyRefs
            .map(surveyRef => toDateOnly(surveyRef.split("|")[1]))
            .filter((date): date is string => date !== null)
            .sort()[0];
    };

    const [selectedTimeRange, setSelectedTimeRange] = useState<TimePair>(
        sessionState.rankOrderTimes[0] ?? [1, 1]
    );

    const [rankOrderOpen, setRankOrderOpen] = useState(false);
    const [rankOrder, setRankOrder] = useState<RankOrderViewModel>(new RankOrderViewModel());

    const lastParamsRef = useRef<string>("");
    useEffect(() => {
        if (surveysLoading) return;

        getData();
    }, [sessionState.rankOrderSort, sessionState.rankOrderDir, sessionState.rankOrderPage, sessionState.rankOrderRows, selectedTimeRange, vehicleType, surveysLoading, selectedSurveyRefsKey]);

    useEffect(() => {
        if (surveysLoading) return;

        const multimodal = sessionState.landusePage.isMultiModal;
        const atc = !multimodal && sessionState.primaryFilters.surveyTypes.includes(2) && sessionState.atcCount > 0;
        const servicing = sessionState.primaryFilters.serviceVehicles.includes("Y");
        const startDate = getModesStartDate();
        const startDateQuery = startDate ? `&startdate=${startDate}` : "";
        post(`TripRateCalculation`, `GetModes?atc=${atc}&servicing=${servicing}&multimodal=${multimodal}${startDateQuery}`, selectedSurveyRefs, setModes);
    }, [surveysLoading, selectedSurveyRefsKey]);

    useEffect(() => {
        if (selectionParam !== null && selectionParam !== '') {
            switch (selectionParam) {
                case 'tripRate':
                    setClickToggleValue('calculate');
                    break;
                case 'site':
                default:
                    setClickToggleValue('site');
                    break;
            }
        }
    }, [selectionParam])

    useEffect(() => {
        if (tabParam !== null && tabParam !== '') {
            switch (tabParam) {
                case 'rankGraph':
                    setShowGraph(true);
                    break;
                case 'rankTable':
                default:
                    setShowGraph(false);
                    break;
            }
        }
    }, [tabParam])

    const getReport = async (exportFilters: RankOrderExportViewModel) => {
        exportFilters = { ...exportFilters, vehicleType: vehicleType };
        const data = new RankOrderPDFViewModel();
        var filter: TripRateCalculationFiltersViewModel = {
            tripRateMain: sessionState.tripRateMain,
            tripRateSub: sessionState.tripRateSub,
            primaryFilters: sessionState.primaryFilters,
            secondaryFilters: sessionState.secondaryFilters,
            landUsePage: sessionState.landusePage
        };

        data.rankOrderVm = exportData;
        data.filtersVm = filter;
        data.autoRemovedSurveysCountList = sessionState.selectedSitesAndSurveys.filter(f => !f.siteActive).map(m => m.siteRef).length;
        data.manuallyRemovedSurveysCount = sessionState.selectedSitesAndSurveys.filter(f => f.reasonForSiteRemoval != null || f.reasonForSurveyRemoval != null).length;
        data.selectedSurveys = selectedSurveyRefs;
        data.hourly = true;
        data.selectedTimeFrames = sessionState.rankOrderTimes;
        exportData.selectedRange = `${getLabel(selectedTimeRange.start)} - ${getLabel(selectedTimeRange.end)}`;
        exportData.percentile15 = rankOrder.percentile15;
        exportData.percentile85 = rankOrder.percentile85;
        data.exportFiltersVm = exportFilters;
        data.rankOrderVm.vehicleType = vehicleType;
        data.sortBy = sessionState.rankOrderSort
        data.explanatoryCommentsIncluded = exportFilters.explanatoryCommentsIncluded;
        data.userComments = exportFilters.userComments;
        const reportTitle = data.exportFiltersVm.reportTitle?.trim();
        const deselected: DeselectedSurveysViewModel[] = [];
        sessionState.selectedSitesAndSurveys
            .filter(f => !f.siteActive || !f.surveyActive)
            .forEach((item: SurveyViewModel) => {
                deselected.push({
                    reference: item.siteRef,
                    surveyDate: item.date,
                    reasonForDeselection: item.reasonForSurveyRemoval == "Removed in Site Selection" ? item.reasonForSiteRemoval : item.reasonForSurveyRemoval,
                    manuallyRemoved: item.manuallyRemoved
                });
            });
        data.exportFiltersVm.deselectedSurveys = deselected;
        data.selectedSurveys = selectedSurveyRefs;
        const requestData: GetRankOrderTableViewModel = {
            mainLandUse: sessionState.tripRateMain,
            subUse: sessionState.tripRateSub,
            orderBy: sessionState.rankOrderSort,
            order: sessionState.rankOrderDir,
            page: null,
            pageRows: sessionState.rankOrderRows,
            headerIndex: sessionState.primaryFilters.headerIndex,
            vehicleType: [vehicleType],
            selectedSurveys: selectedSurveyRefs,
            startPeriod: selectedTimeRange.start,
            endPeriod: selectedTimeRange.end,
            selectedDays: sessionState.primaryFilters.daysOfWeek,
            isPeakHour: sessionState.rankOrderPeakHour,
            selectedPeriodLabel: formatPair(selectedTimeRange)
        };

        data.exportFiltersVm.exportType === 'csv' ?
            postCSV("CsvGenerator", `GetRankOrderCSV?sortBy=${data.sortBy}`, reportTitle ? reportTitle : "Rank Order", requestData) :
            postPDF("PDFGenerator", "GetRankOrderPDF", reportTitle ? reportTitle : "Rank Order", false, data);
    };

    useEffect(() => {
        setExportData(rankOrder)
    }, [rankOrder]);

    const getData = () => {
        const requestData: GetRankOrderTableViewModel = {
            mainLandUse: sessionState.tripRateMain,
            subUse: sessionState.tripRateSub,
            orderBy: sessionState.rankOrderSort,
            order: sessionState.rankOrderDir,
            page: sessionState.rankOrderPage,
            pageRows: sessionState.rankOrderRows,
            headerIndex: sessionState.primaryFilters.headerIndex,
            vehicleType: [vehicleType],
            selectedSurveys: selectedSurveyRefs,
            startPeriod: selectedTimeRange.start,
            endPeriod: selectedTimeRange.end,
            selectedDays: sessionState.primaryFilters.daysOfWeek,
            isPeakHour: sessionState.rankOrderPeakHour,
            selectedPeriodLabel: formatPair(selectedTimeRange)
        };

        const key = JSON.stringify(requestData);
        if (key === lastParamsRef.current) {
            return;
        }
        lastParamsRef.current = key;

        post("RankOrder", `GetRankOrderTable`, requestData, setRankOrder);
    };

    const closeDialog = (refresh?: boolean) => {
        setRankOrderOpen(false);
        setExportDialog(false);
        setSelectedTimeRange(sessionState.rankOrderTimes[0]);
    };

    const handleTimeRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const [start, end] = (e.target.value as string).split("-").map(Number);
        setSelectedTimeRange({ start, end });
    };
    const openPDFDialog = () => {
        setExportDialog(true);
    };

    const handleVehicleChange = (id: number, name: string) => () => {
        setVehicleType(id);
        setVehicleLabel(name)
    };

    const toggleGraph = () => {
        const nextShowGraph = !showGraph;
        const nextParams = new URLSearchParams(location.search);
        nextParams.set('toTab', nextShowGraph ? 'rankGraph' : 'rankTable');
        navigate(
            { pathname: location.pathname, search: nextParams.toString(), hash: location.hash },
            { replace: true }
        );
        setShowGraph(nextShowGraph);
    };

    const openSiteTripRateGraph = () => {
        setSiteGraphOpen(true);
        setSiteGraphView("selection");
        setSiteGraphMode(null);
        setSiteGraphDirection("Total");
    };

    const closeSiteTripRateGraph = () => {
        setSiteGraphOpen(false);
    };

    return (
        <LicenceCheck requriedLicence={['Education', 'Single Office (Unlimited Access)', 'Multi-Office', 'Single Office (Single Access)']}>
            <Grid container>
                <Grid size={5}>
                    <DataContainer
                        title={`Rank Order For: ${rankOrder.abbreviation} Calculated on: ${sessionState.rankOrderPeakHour ? 'Peak Hour' : formatPair(selectedTimeRange)}`}
                        headerRight={
                            <Button fullWidth={false} disabled={loading} onClick={openPDFDialog}>
                                Export
                            </Button>
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="self-end">
                            <Grid>
                                <Stack>
                                    <Typography variant='body2'>Search</Typography>
                                    <TextField value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
                                </Stack>
                            </Grid>
                            <Grid sx={{ flexShrink: 0 }}>
                                <Button
                                    fullWidth={false}
                                    disabled={loading}
                                    onClick={() => setRankOrderOpen(true)}
                                    sx={{ width: 145, height: 46, whiteSpace: 'nowrap' }}
                                >
                                    Recalculate
                                </Button>
                            </Grid>
                            <Grid sx={{ flexShrink: 0 }}>
                                <Button
                                    fullWidth={false}
                                    disabled={loading}
                                    onClick={toggleGraph}
                                    sx={{ width: 145, height: 46, whiteSpace: 'nowrap' }}
                                >
                                    {!showGraph ? 'Show Graph' : 'Show Table'}
                                </Button>
                            </Grid>
                            {!sessionState.rankOrderPeakHour && (
                                <FormControl>
                                    <RadioGroup
                                        row
                                        value={`${selectedTimeRange.start}-${selectedTimeRange.end}`}
                                        onChange={handleTimeRangeChange}
                                    >
                                        {sessionState.rankOrderTimes.map((pair) => {
                                            const key = `${pair.start}-${pair.end}`;
                                            return (
                                                <FormControlLabel
                                                    key={key}
                                                    value={key}
                                                    control={<Radio />}
                                                    label={formatPair(pair)}
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>
                            )}
                        </Stack>
                    </DataContainer>
                </Grid>

                <Grid size={3}>
                    <DataContainer title="Percentiles">
                        <Stack spacing={1}>
                            <Typography variant="body2">Total Count: {rankOrder.count}</Typography>
                            <Stack direction="row" spacing={1}>
                                <Typography variant="body2">
                                    15th Percentile: {rankOrder.percentile15}{' '}
                                    {rankOrder.percentile15Rank}
                                </Typography>
                                <Typography variant="body2">Total: {rankOrder.totalPercentile15}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Typography variant="body2">
                                    85th Percentile: {rankOrder.percentile85}{' '}
                                    {rankOrder.percentile85Rank}
                                </Typography>
                                <Typography variant="body2">Total: {rankOrder.totalPercentile85}</Typography>
                                {(rankOrder.count ?? 0) < 20 && (
                                    <WarningPopup
                                        title="Percentile Warning"
                                        content="Using 85th and 15th percentile highlighted trip rates in data sets of under 20 surveys is not recommended by TRICS, and may be misleading."
                                        type="error"
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </DataContainer>
                </Grid>

                {!sessionState.rankOrderPeakHour && (
                    <>
                        <Grid size={2}>
                            <DataContainer title="Median Values">
                                <Typography variant="body2">Arrivals: {rankOrder.arrivalMedian}</Typography>
                                <Typography variant="body2">Departures: {rankOrder.departureMedian}</Typography>
                                <Typography variant="body2">Totals: {rankOrder.totalMedian}</Typography>
                            </DataContainer>
                        </Grid>

                        <Grid size={2}>
                            <DataContainer title="Mean Values">
                                <Typography variant="body2">Arrivals: {rankOrder.arrivalMean}</Typography>
                                <Typography variant="body2">Departures: {rankOrder.departureMean}</Typography>
                                <Typography variant="body2">Totals: {rankOrder.totalMean}</Typography>
                            </DataContainer>
                        </Grid>
                    </>
                )}
                <Grid container size={12}>
                    <Grid container size={3} component={Paper}>
                        <Grid size={12}>
                            <Button disabled={loading} onClick={openSiteTripRateGraph}>Trip Rate Line Graph</Button>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="h6">Trip Rate Mode</Typography>
                        </Grid>
                        <Line />
                        <Grid size={12}>
                            <Typography variant="h6">Toggle Click Functionality</Typography>
                        </Grid>
                        <Grid size={12}>
                            <RadioGroup
                                value={clickToggleValue}
                                onChange={(e) => setClickToggleValue(e.target.value)}>
                                <FormControlLabel value="site" control={<Radio />} label="Site Navigation" />
                                <FormControlLabel value="calculate" control={<Radio />} label="Trip Rate Calculator" />
                            </RadioGroup>
                        </Grid>
                        <Line />
                        <Grid size={12}>
                            <MenuList>
                                {modes.map((mode: ModeSelectionViewModel) =>
                                    <MenuItem key={mode.modeId} value={mode.modeId} onClick={handleVehicleChange(mode.modeId, mode.modeName)} disabled={!mode.isSelectable} selected={vehicleType == mode.modeId}>
                                        {mode.isSelectable ?
                                            <Avatar
                                                variant="rounded"
                                                sx={{ width: 30, height: 30, marginRight: '8px', bgcolor: vehicleType == mode.modeId ? theme.palette.primary.main : "gray" }}
                                                src={`/Icons/${mode.modeIcon}`} /> :
                                            <Avatar
                                                variant="rounded"
                                                sx={{ width: 30, height: 30, marginRight: '8px', bgcolor: "gray" }}
                                                src={`/Icons/${mode.modeIcon}`} />
                                        }
                                        <Typography variant="caption">{mode.modeName}</Typography>
                                        <Box flexGrow={1} />
                                        {mode.isSelectable ?
                                            <Typography variant="caption">{`${mode.modeCount} Surveys`}</Typography>
                                            :
                                            <Typography variant="caption">{mode.notes}</Typography>

                                        }
                                    </MenuItem>
                                )}
                            </MenuList>
                        </Grid>
                        <Grid size={12}>
                            <PDFLink label="Click Here for rules on calculating trips by count type" container="GeneralDocs" file="Calculation by Mode" />
                        </Grid>

                    </Grid>
                    <Grid size={9}>
                        {showGraph ? (
                            <RankOrderScatterPlot pair={selectedTimeRange} calculation={rankOrder.abbreviation ?? ''} vehicleTypeId={vehicleType} vehicleTypeLabel={vehicleLabel} timeRange={formatPair(selectedTimeRange)} clickToggle={clickToggleValue} surveyRefs={selectedSurveyRefs} />
                        ) : (
                            <RankOrderTable rankOrder={rankOrder} count={rankOrder.count ?? 0} clickToggle={clickToggleValue} searchValue={searchValue} />
                        )}
                    </Grid>
                </Grid>

                <RankOrderPeriodDialog dialogOpen={rankOrderOpen} handleClose={closeDialog} />
            </Grid>

            <RankOrderExportDialog handleClose={closeDialog} dialogOpen={exportDialog} exportRankOrder={getReport} rankOrder={rankOrder} />
            <TripRateSitesGraphDialog
                open={siteGraphOpen}
                onClose={closeSiteTripRateGraph}
                origin="rankOrder"
                returnLabel="Back to Rank Order"
                returnTo={`${location.pathname}${location.search}`}
                selectedMode={siteGraphMode}
                direction={siteGraphDirection}
                view={siteGraphView}
                allowModeSelection={true}
                availableModes={modes}
                onSelectedModeChange={setSiteGraphMode}
                onDirectionChange={setSiteGraphDirection}
                onViewChange={setSiteGraphView}
            />
        </LicenceCheck>
    );
}
